import { useCallback, useEffect, useState } from "react";
import { CurrencyFlag } from "../components/CurrencyFlag.jsx";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { http } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { RefreshCw, ShieldCheck, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { TwoFactorChallenge } from "../components/TwoFactorChallenge";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 0.06 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Login() {
  const { user, login, finalizeSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState({ id: "", question: "", loading: true });
  const [captchaText, setCaptchaText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [rates, setRates] = useState([]);
  // 2FA step machine: { mode: "credentials" | "setup" | "verify", pendingToken, email }
  const [stage, setStage] = useState({ mode: "credentials" });

  // Live exchange rates from the database (public endpoint — no auth needed).
  // NEVER show hardcoded numbers: we only render real DB rates so the login
  // board can never drift out of sync. Refreshes every 60s while page is open.
  useEffect(() => {
    let active = true;
    const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("id-ID");
    const fetchRates = async () => {
      try {
        const { data } = await http.get("/public/rates", {
          params: { _: Date.now() },                    // cache-buster
          headers: { "Cache-Control": "no-cache" },
        });
        if (!active || !Array.isArray(data) || data.length === 0) return;
        const preferred = ["USD", "EUR", "SGD", "JPY"];
        const byCode = Object.fromEntries(data.map((r) => [r.code, r]));
        const picked = preferred.filter((c) => byCode[c]).map((c) => byCode[c]);
        const rest = data.filter((r) => !preferred.includes(r.code));
        const list = [...picked, ...rest].slice(0, 4);
        setRates(list.map((r, i) => ({
          code: r.code, flag: r.flag,
          buy: fmt(r.buy_rate), sell: fmt(r.sell_rate),
          hot: r.code === "USD" || (byCode.USD ? false : i === 0),
        })));
      } catch { /* keep whatever we last had; show skeleton if none yet */ }
    };
    fetchRates();
    const t = setInterval(fetchRates, 60000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const loadCaptcha = useCallback(async () => {
    setCaptcha((c) => ({ ...c, loading: true }));
    setCaptchaText("");
    try {
      const { data } = await http.get("/auth/captcha");
      setCaptcha({ id: data.captcha_id, question: data.question, loading: false });
    } catch {
      setCaptcha({ id: "", question: "", loading: false });
      toast.error("Gagal memuat kode verifikasi");
    }
  }, []);

  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  if (user) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!captchaText.trim()) return toast.error("Isi kode verifikasi dulu");
    setLoading(true);
    const res = await login(email, password, { id: captcha.id, text: captchaText });
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Login gagal");
      loadCaptcha();
      return;
    }
    const data = res.data || {};
    if (data.status === "authenticated") {
      toast.success("Login berhasil");
      navigate("/admin/dashboard");
    } else if (data.status === "2fa_required") {
      toast.success("Kode verifikasi telah dikirim ke email Anda");
      setStage({ mode: "verify", pendingToken: data.pending_token, email: data.email || email, emailMasked: data.email_masked });
    } else {
      // Backward-compatible: server returned a session directly.
      toast.success("Login berhasil");
      navigate("/admin/dashboard");
    }
  };

  const onTwoFactorAuthenticated = (data) => {
    finalizeSession(data);
    navigate("/admin/dashboard");
  };
  const backToLogin = () => {
    setStage({ mode: "credentials" });
    setPassword("");
    setCaptchaText("");
    loadCaptcha();
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[1.05fr_0.95fr] bg-white font-manrope">
      {/* ============ LEFT PANEL — immersive dark hero (desktop) ============ */}
      <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16 relative overflow-hidden" style={{ background: "#000000" }}>
        {/* Uploaded currency-blueprint backdrop — kept subtle so text & logo stay legible */}
        <div className="absolute inset-0 z-0 bg-center bg-cover opacity-40"
          style={{ backgroundImage: "url('https://customer-assets-m6fa6gv7.emergentagent.net/job_forex-exchange-64/artifacts/c1m6llvt_Monochrome%20Floating%20Currency%20Blueprint.png')" }} />
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0.6))" }} />
        {/* Deep black backdrop + subtle yellow glow accents */}
        <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full z-0 blur-3xl" style={{ background: "rgba(246,238,35,0.08)" }} />
        <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full z-0 blur-3xl" style={{ background: "rgba(246,238,35,0.05)" }} />

        {/* Brand */}
        <motion.div initial="hidden" animate="show" custom={0} variants={fade} className="relative z-10">
          <BrandLogo size="lg" variant="dark" />
          <div className="mt-3 flex items-center gap-2">
            <span className="inline-block h-1 w-10 rounded-full" style={{ background: "#F6EE23" }} />
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/80">PT Emerald Valasindo</div>
          </div>
        </motion.div>

        {/* Middle — headline + glass rate cards */}
        <div className="relative z-10 space-y-8">
          <motion.div initial="hidden" animate="show" custom={1} variants={fade}>
            <h2 className="font-outfit text-4xl xl:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Credibility<br />
              <span style={{ color: "#F6EE23" }}>&amp; Reliability</span>
            </h2>
          </motion.div>

          <motion.div initial="hidden" animate="show" custom={2} variants={fade} className="grid grid-cols-2 gap-3.5 max-w-md">
            {rates.length === 0
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} data-testid="login-rate-skeleton"
                    className={`relative rounded-2xl p-4 backdrop-blur-xl bg-white/[0.06] border border-white/10 shadow-2xl ${i % 2 === 0 ? "floaty" : "floaty-slow"}`}>
                    <div className="h-3 w-12 rounded bg-white/10 animate-pulse" />
                    <div className="mt-3 h-5 w-24 rounded bg-white/10 animate-pulse" />
                    <div className="mt-2 h-3 w-20 rounded bg-white/5 animate-pulse" />
                  </div>
                ))
              : rates.map((r, i) => (
              <div
                key={r.code}
                data-testid={`login-rate-${r.code}`}
                className={`relative rounded-2xl p-4 backdrop-blur-xl bg-white/[0.06] border border-white/10 shadow-2xl ${i % 2 === 0 ? "floaty" : "floaty-slow"}`}
                style={r.hot ? { borderLeft: "3px solid #F6EE23" } : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                    <CurrencyFlag code={r.code} emoji={r.flag} className="mr-1" /> {r.code}
                  </div>
                  {r.hot && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-black" style={{ background: "#F6EE23" }}>TOP</span>
                  )}
                </div>
                <div className="font-mono-num mt-2.5 text-xl font-semibold text-white leading-none">{r.buy}</div>
                <div className="font-mono-num text-xs text-zinc-500 mt-1">jual / {r.sell}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Bottom — footer */}
        <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="relative z-10">
          <div className="text-[11px] text-zinc-600">© 2026 PT Emerald Valasindo · v1.0</div>
        </motion.div>
      </div>

      {/* ============ RIGHT / MOBILE FORM ============ */}
      <div className="relative min-h-screen flex flex-col bg-white overflow-hidden">
        {/* MOBILE-ONLY dark hero backdrop */}
        <div className="lg:hidden absolute inset-x-0 top-0 h-60 pointer-events-none z-0" style={{ background: "#000000" }} aria-hidden>
          <div className="absolute -top-10 right-6 h-40 w-40 rounded-full blur-3xl" style={{ background: "rgba(246,238,35,0.08)" }} />
          <svg viewBox="0 0 400 60" preserveAspectRatio="none" className="absolute -bottom-1 left-0 right-0 w-full h-14">
            <path d="M0,40 C120,0 280,60 400,20 L400,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>

        <div className="relative z-10 flex-1 flex items-start lg:items-center justify-center px-5 pt-8 pb-8 sm:pt-12 lg:p-10 xl:p-14">
          <div className="w-full max-w-md lg:max-w-sm">
            {/* Mobile brand block */}
            <motion.div initial="hidden" animate="show" custom={0} variants={fade} className="lg:hidden mb-6 text-center">
              <BrandLogo size="xl" variant="dark" className="mx-auto" />
              <div className="mt-3 flex items-center justify-center gap-2">
                <span className="inline-block h-[3px] w-8 rounded-full" style={{ background: "#F6EE23" }} />
                <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-white/85">PT Emerald Valasindo</div>
                <span className="inline-block h-[3px] w-8 rounded-full" style={{ background: "#F6EE23" }} />
              </div>
            </motion.div>

            {/* Desktop eyebrow */}
            <motion.div initial="hidden" animate="show" custom={0} variants={fade} className="hidden lg:flex mb-7 items-center gap-2">
              <span className="inline-block h-1 w-10 rounded-full" style={{ background: "#F6EE23" }} />
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">Portal Kasir &amp; Admin</span>
            </motion.div>

            <div className="lg:bg-transparent bg-white rounded-3xl lg:rounded-none shadow-xl lg:shadow-none border lg:border-0 border-slate-200 p-6 sm:p-7 lg:p-0" data-testid="login-card">
              {stage.mode !== "credentials" ? (
                <TwoFactorChallenge
                  pendingToken={stage.pendingToken}
                  email={stage.email}
                  emailMasked={stage.emailMasked}
                  onAuthenticated={onTwoFactorAuthenticated}
                  onBack={backToLogin}
                />
              ) : (
              <>
              <motion.h1 initial="hidden" animate="show" custom={1} variants={fade} className="font-outfit text-3xl lg:text-4xl font-extrabold tracking-tight text-zinc-900">
                Selamat datang kembali
              </motion.h1>
              <motion.p initial="hidden" animate="show" custom={2} variants={fade} className="text-zinc-500 mt-2 text-sm">
                Masuk untuk melanjutkan operasional harian Anda.
              </motion.p>

              <form onSubmit={submit} className="mt-7 space-y-4" autoComplete="off">
                {/* Email */}
                <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="space-y-1.5">
                  <Label htmlFor="email" className="text-[11px] font-bold tracking-wide text-zinc-800 uppercase">Email</Label>
                  <div className="relative group">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none transition-colors group-focus-within:text-zinc-900" />
                    <Input id="email" type="email" data-testid="login-email"
                      autoComplete="username" inputMode="email"
                      className="pl-11 h-12 rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-yellow-300/60 text-base transition-colors"
                      placeholder="nama@perusahaan.com"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </motion.div>

                {/* Password */}
                <motion.div initial="hidden" animate="show" custom={4} variants={fade} className="space-y-1.5">
                  <Label htmlFor="password" className="text-[11px] font-bold tracking-wide text-zinc-800 uppercase">Password</Label>
                  <div className="relative group">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none transition-colors group-focus-within:text-zinc-900" />
                    <Input id="password" type={showPass ? "text" : "password"} data-testid="login-password"
                      autoComplete="current-password"
                      className="pl-11 pr-11 h-12 rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-yellow-300/60 text-base transition-colors"
                      placeholder="••••••••"
                      value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" onClick={() => setShowPass((s) => !s)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors"
                      aria-label={showPass ? "Sembunyikan password" : "Tampilkan password"} data-testid="toggle-password">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </motion.div>

                {/* Captcha */}
                <motion.div initial="hidden" animate="show" custom={5} variants={fade}
                  className="rounded-2xl border border-zinc-200 bg-zinc-50/70 p-3.5 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="captcha" className="text-[11px] font-bold tracking-wide text-zinc-800 uppercase flex items-center gap-1.5">
                      <ShieldCheck size={13} className="text-zinc-900" /> Verifikasi Matematika
                    </Label>
                    <button type="button" onClick={loadCaptcha} disabled={captcha.loading}
                      className="h-8 w-8 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 active:scale-95 transition-transform flex items-center justify-center text-zinc-600 disabled:opacity-50"
                      data-testid="captcha-refresh" aria-label="Muat soal baru">
                      <RefreshCw size={14} className={captcha.loading ? "animate-spin" : ""} />
                    </button>
                  </div>
                  <div className="flex items-center justify-center h-20 rounded-xl border border-zinc-200 bg-white px-2 overflow-hidden select-none" data-testid="captcha-image-wrap">
                    {captcha.loading ? (
                      <span className="text-xs text-zinc-400 animate-pulse">Memuat soal…</span>
                    ) : captcha.question ? (
                      <span className="font-mono-num font-bold text-zinc-900 text-4xl tracking-wider" data-testid="captcha-question" aria-label={`Berapa hasil dari ${captcha.question}`}>
                        {captcha.question}
                      </span>
                    ) : (
                      <span className="text-xs text-rose-500">Gagal memuat</span>
                    )}
                  </div>
                  <Input id="captcha" type="text" inputMode="numeric" spellCheck={false} autoComplete="off"
                    placeholder="Ketik jawabannya di sini"
                    value={captchaText} onChange={(e) => setCaptchaText(e.target.value.replace(/[^0-9]/g, ""))}
                    data-testid="login-captcha" required maxLength={4}
                    className="h-11 rounded-xl font-mono-num tracking-[0.4em] text-center text-lg bg-white border-zinc-200 focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-yellow-300/60 transition-colors placeholder:tracking-normal placeholder:text-sm" />
                  <div className="text-[11px] text-zinc-400">Ketik hasil penjumlahan di atas. Berlaku 5 menit.</div>
                </motion.div>

                <motion.div initial="hidden" animate="show" custom={6} variants={fade}>
                  <Button type="submit"
                    className="group w-full h-14 rounded-xl text-black text-base font-extrabold tracking-wide shadow-lg shadow-yellow-400/25 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-transform"
                    style={{ background: "#F6EE23" }}
                    disabled={loading || captcha.loading} data-testid="login-submit">
                    <span className="flex items-center justify-center gap-2">
                      {loading ? "Memproses…" : "Masuk"}
                      {!loading && <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />}
                    </span>
                  </Button>
                </motion.div>
              </form>
              </>
              )}
            </div>

            <div className="lg:hidden text-center mt-6 text-[11px] text-zinc-400">© 2026 PT Emerald Valasindo · v1.0</div>
          </div>
        </div>
      </div>
    </div>
  );
}
